'use client';

import {
    faChevronDown,
    faChevronRight,
    faCircleCheck,
    faCircleExclamation,
    faRotateRight,
    faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Skeleton, Spinner, Tooltip } from '@ui';
import { StyledCheckbox } from '@ui/Form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';
import CellEditor from './CellEditor';
import { ColumnKey, ImportDocument, ImportField, isBookable, readValue } from './types';

interface Props {
    documents: ImportDocument[];
    fields: ImportField[];
    selected: Set<number>;
    activeId: number | null;
    onToggle: (id: number) => void;
    onToggleAll: (checked: boolean) => void;
    onPreview: (id: number) => void;
    onChange: (id: number, key: ColumnKey, value: any) => void;
    onRetry: (id: number) => void;
    onDiscard: (id: number) => void;
}

const isEmpty = (value: any): boolean => {
    if (null === value || undefined === value || '' === value || false === value) return true;
    // a table is a grid of booleans, an address an object of strings
    if (Array.isArray(value)) return value.every(isEmpty);
    if (typeof value === 'object') return Object.values(value).every(isEmpty);

    return false;
};

/** Messages of a field, including nested ones like `form_data.address.city`. */
const messagesFor = (byKey: Record<string, string[]> | undefined, key: ColumnKey) =>
    Object.entries(byKey ?? {})
        .filter(([errorKey]) => errorKey === key || errorKey.startsWith(`${key}.`))
        .flatMap(([, messages]) => messages);

/**
 * One row per uploaded file. Name and email stay visible, the form fields fold away behind a
 * count of what is still missing, so a stack of forms reads like a checklist.
 */
const ReviewTable = ({
    documents,
    fields,
    selected,
    activeId,
    onToggle,
    onToggleAll,
    onPreview,
    onChange,
    onRetry,
    onDiscard
}: Props) => {
    const { t } = useTranslation('bookings');
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    const allSelected = documents.length > 0 && documents.every((doc) => selected.has(doc.id));
    const allExpanded = documents.length > 0 && documents.every((doc) => expanded.has(doc.id));

    const toggleExpanded = (id: number) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2">
                <StyledCheckbox
                    isSelected={allSelected}
                    onChange={onToggleAll}
                    label={t('booking.documentImport.selectAll')}
                />
                {fields.length > 0 && (
                    <Button
                        size="sm"
                        flat
                        color="light"
                        onPress={() => setExpanded(allExpanded ? new Set() : new Set(documents.map((doc) => doc.id)))}
                    >
                        {allExpanded ? t('booking.documentImport.collapseAll') : t('booking.documentImport.expandAll')}
                    </Button>
                )}
            </div>

            {documents.map((doc) => {
                const isReading = 'pending' === doc.status || 'processing' === doc.status;
                const isOpen = expanded.has(doc.id);
                const invalidFields = fields.filter(
                    (field) => messagesFor(doc.errors, `form_data.${field.name}`).length > 0
                ).length;
                const missingFields = fields.filter(
                    (field) => messagesFor(doc.warnings, `form_data.${field.name}`).length > 0
                ).length;
                const filledFields = fields.filter(
                    (field) => !isEmpty(readValue(doc.values, `form_data.${field.name}`))
                ).length;

                const renderEditor = (key: ColumnKey, field: ImportField | null) => {
                    const errors = messagesFor(doc.errors, key);
                    const hints = [...errors, ...messagesFor(doc.warnings, key)];

                    return (
                        <Tooltip tooltip={hints.join(' ')} disabled={hints.length === 0}>
                            <CellEditor
                                field={field}
                                columnKey={key}
                                value={readValue(doc.values, key)}
                                isInvalid={errors.length > 0}
                                onChange={(value) => onChange(doc.id, key, value)}
                            />
                        </Tooltip>
                    );
                };

                return (
                    <div
                        key={doc.id}
                        className={cn(
                            'tw-rounded-xl tw-border tw-border-solid tw-border-accents-2',
                            activeId === doc.id && 'tw-bg-accents-1'
                        )}
                    >
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-p-2">
                            <StyledCheckbox isSelected={selected.has(doc.id)} onChange={() => onToggle(doc.id)} />

                            <div className="tw-flex tw-w-48 tw-min-w-0 tw-flex-col">
                                <button
                                    type="button"
                                    className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-sm"
                                    onClick={() => onPreview(doc.id)}
                                >
                                    <StatusIcon doc={doc} />
                                    <span className="tw-truncate tw-underline">
                                        {doc.original_name ?? `#${doc.id}`}
                                    </span>
                                </button>
                                {'failed' === doc.status && (
                                    <span className="tw-mt-1 tw-text-xs tw-text-error">
                                        {t(`booking.documentImport.errors.${doc.error_code ?? 'ai_failed'}`, {
                                            defaultValue: t('booking.documentImport.errors.ai_failed')
                                        })}
                                    </span>
                                )}
                            </div>

                            {isReading ? (
                                // same slots as the finished row, so nothing jumps when the values arrive
                                <>
                                    <div className="tw-min-w-[160px] tw-flex-1">
                                        <Skeleton rounded={false} className="tw-h-8 tw-rounded-xl" />
                                    </div>
                                    <div className="tw-min-w-[200px] tw-flex-1">
                                        <Skeleton rounded={false} className="tw-h-8 tw-rounded-xl" />
                                    </div>
                                    <span className="tw-text-sm tw-text-accents-6">
                                        {'pending' === doc.status
                                            ? t('booking.documentImport.status.pending')
                                            : t('booking.documentImport.status.processing')}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <div className="tw-min-w-[160px] tw-flex-1">{renderEditor('name', null)}</div>
                                    <div className="tw-min-w-[200px] tw-flex-1">{renderEditor('email', null)}</div>
                                </>
                            )}

                            {fields.length > 0 && !isReading && (
                                <button
                                    type="button"
                                    className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm"
                                    onClick={() => toggleExpanded(doc.id)}
                                >
                                    <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
                                    <span
                                        className={cn(
                                            invalidFields > 0 && 'tw-text-error',
                                            invalidFields === 0 && missingFields > 0 && 'tw-text-warning',
                                            invalidFields === 0 && missingFields === 0 && 'tw-text-accents-7'
                                        )}
                                    >
                                        {invalidFields > 0 &&
                                            t('booking.documentImport.fields.invalid', { count: invalidFields })}
                                        {invalidFields === 0 &&
                                            missingFields > 0 &&
                                            t('booking.documentImport.fields.missing', { count: missingFields })}
                                        {invalidFields === 0 &&
                                            missingFields === 0 &&
                                            t('booking.documentImport.fields.filled', {
                                                filled: filledFields,
                                                total: fields.length
                                            })}
                                    </span>
                                </button>
                            )}

                            <div className="tw-ml-auto tw-flex tw-gap-1">
                                {'failed' === doc.status && (
                                    <Button
                                        size="sm"
                                        flat
                                        color="light"
                                        aria-label={t('booking.documentImport.actions.retry')}
                                        onPress={() => onRetry(doc.id)}
                                        icon={<FontAwesomeIcon icon={faRotateRight} />}
                                    />
                                )}
                                <Button
                                    size="sm"
                                    flat
                                    color="error"
                                    aria-label={t('booking.documentImport.actions.discard')}
                                    onPress={() => onDiscard(doc.id)}
                                    icon={<FontAwesomeIcon icon={faTrash} />}
                                />
                            </div>
                        </div>

                        {isOpen && !isReading && (
                            <div className="tw-grid tw-grid-cols-1 tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-accents-2 tw-p-3 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
                                {fields.map((field) => (
                                    <div
                                        key={field.name}
                                        className={cn(
                                            'tw-flex tw-flex-col tw-gap-1',
                                            'table' === field.type && 'sm:tw-col-span-2 lg:tw-col-span-3'
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                'tw-text-xs',
                                                messagesFor(doc.warnings, `form_data.${field.name}`).length > 0
                                                    ? 'tw-text-warning'
                                                    : 'tw-text-accents-7'
                                            )}
                                        >
                                            {field.label || field.name}
                                            {field.rules?.includes('required') && ' *'}
                                        </span>
                                        {renderEditor(`form_data.${field.name}`, field)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const StatusIcon = ({ doc }: { doc: ImportDocument }) => {
    const { t } = useTranslation('bookings');

    if ('pending' === doc.status || 'processing' === doc.status) {
        return <Spinner size="sm" color="success" />;
    }

    const bookable = isBookable(doc);

    return (
        <Tooltip
            tooltip={
                bookable ? t('booking.documentImport.status.bookable') : t('booking.documentImport.status.incomplete')
            }
        >
            <FontAwesomeIcon
                icon={bookable ? faCircleCheck : faCircleExclamation}
                className={bookable ? 'tw-text-success' : 'tw-text-warning'}
            />
        </Tooltip>
    );
};

export default ReviewTable;
